import React, { useEffect, useMemo, useRef } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import isEqual from "lodash-es/isEqual"

import { useEvent } from "shared/lib/useEvent"
import { UpdateFollowUpConfigurationInput } from "shared/types/__generated__/graphql"
import { Button } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Toggle } from "shared/ui/input/boolean/toggle/Toggle"

import { FollowUpConfiguration } from "entities/dim-configuration/types/FollowUpConfiguration"

import { MoveDirection } from "../../../shared/types/MoveDirection"
import { useFollowUpConfigurationRowDnD } from "../model/useFollowUpConfigurationRowDnD"
import { FollowUpConfigurationDeleteButton } from "./FollowUpConfigurationDeleteButton"

interface RowFormData {
  isActive: boolean
}

export interface FollowUpConfigurationRowProps {
  entry: FollowUpConfiguration
  productImage?: React.ReactNode
  productKind?: string
  groupID: string
  isFirst: boolean
  isLast: boolean
  insertBefore?: boolean
  insertAfter?: boolean
  onUpdate: (id: string, input: UpdateFollowUpConfigurationInput) => void
  onMove: (id: string, direction: MoveDirection) => void
  onDelete: (id: string) => Promise<void>
  isUpdating?: boolean
  isDeleting?: boolean
  isReorderDisabled?: boolean
}

export const FollowUpConfigurationRow = React.memo(
  ({
    entry,
    productImage,
    productKind,
    groupID,
    isFirst,
    isLast,
    insertBefore,
    insertAfter,
    onUpdate,
    onMove,
    onDelete,
    isUpdating,
    isDeleting,
    isReorderDisabled
  }: FollowUpConfigurationRowProps) => {
    const { t } = useTranslation()

    const values: RowFormData = useMemo(
      () => ({ isActive: entry.isActive ?? false }),
      [entry.isActive]
    )

    const form = useForm<RowFormData>({ defaultValues: values })
    const persistedValues = useRef(values)

    useEffect(() => {
      persistedValues.current = values
      form.reset(values)
    }, [values, form.reset])

    useEffect(() => {
      const unsubscribe = form.subscribe({
        formState: { values: true },
        callback: ({ values: nextValues }) => {
          if (isEqual(nextValues, persistedValues.current)) return

          const nextEntry: RowFormData = {
            isActive: nextValues.isActive ?? false
          }
          persistedValues.current = nextEntry

          onUpdate(entry.id, { isActive: nextEntry.isActive })
        }
      })
      return () => unsubscribe()
    }, [form, entry, onUpdate])

    const {
      dndRef,
      dragAttributes,
      dragListeners,
      setDragHandleRef,
      isDragging
    } = useFollowUpConfigurationRowDnD({
      entry,
      groupID,
      disabled: isUpdating || isReorderDisabled
    })

    const handleMoveUp = useEvent(() => onMove(entry.id, MoveDirection.UP))
    const handleMoveDown = useEvent(() => onMove(entry.id, MoveDirection.DOWN))

    return (
      <FormProvider {...form}>
        <form
          className="follow-up-config-admin-page__row"
          ref={dndRef}
          data-dragging={isDragging ? "" : undefined}
          data-insert-before={insertBefore && !isDragging ? "" : undefined}
          data-insert-after={insertAfter && !isDragging ? "" : undefined}
        >
          <Toggle.Root
            name="isActive"
            label={t("FollowUpConfiguration.Fields.IsActive")}
            className="follow-up-config-admin-page__toggle"
          >
            <Toggle.Input />
          </Toggle.Root>
          <div className="follow-up-config-admin-page__row-order">
            {/* dnd-kit only registers a mouse and a touch sensor, so the arrows
                stay as the keyboard-accessible way to reorder a row. */}
            <Button
              variant="tertiary"
              className="btn--borderless"
              onClick={handleMoveUp}
              disabled={isFirst || isUpdating || isReorderDisabled}
              isIconOnly
              icon={IconName.CARET_UP}
              label={t("FollowUpConfiguration.Actions.MoveUp")}
            />
            <Button
              variant="tertiary"
              className="btn--borderless"
              onClick={handleMoveDown}
              disabled={isLast || isUpdating || isReorderDisabled}
              isIconOnly
              icon={IconName.CARET_DOWN}
              label={t("FollowUpConfiguration.Actions.MoveDown")}
            />
          </div>
          <Button
            variant="tertiary"
            className="btn--borderless follow-up-config-admin-page__row-handle"
            ref={setDragHandleRef}
            isIconOnly
            icon={IconName.DRAG_VERTICAL}
            label={t("FollowUpConfiguration.Actions.Drag")}
            disabled={isUpdating || isReorderDisabled}
            {...dragAttributes}
            {...dragListeners}
          />

          <div className="follow-up-config-admin-page__row-product">
            {productImage ? (
              <div className="follow-up-config-admin-page__row-product-image">
                {productImage}
              </div>
            ) : null}
            <div className="follow-up-config-admin-page__row-product-titles">
              <span className="follow-up-config-admin-page__row-product-name">
                {entry.targetProduct.productName}
              </span>
              {productKind ? (
                <span className="follow-up-config-admin-page__row-product-kind">
                  {productKind}
                </span>
              ) : null}
              {entry.targetProduct.isActive === false ? (
                <span className="follow-up-config-admin-page__row-warning">
                  {t("FollowUpConfiguration.Hints.TargetProductInactive")}
                </span>
              ) : null}
            </div>
          </div>

          <div className="follow-up-config-admin-page__row-position">
            <span className="follow-up-config-admin-page__row-position-label">
              {t("FollowUpConfiguration.Fields.Position")}
            </span>
            <span className="follow-up-config-admin-page__row-position-value">
              {entry.position}
            </span>
          </div>

          <div className="follow-up-config-admin-page__row-actions">
            <FollowUpConfigurationDeleteButton
              id={entry.id}
              onDelete={onDelete}
              disabled={isDeleting}
            />
          </div>
        </form>
      </FormProvider>
    )
  }
)

FollowUpConfigurationRow.displayName = "FollowUpConfigurationRow"
